// frontend/src/components/admin/LicenceModulesManager.jsx
// ============================================================================
// GESTIONNAIRE MODULES & LICENCES
// Grille modules + Tarifs / Frais / Limites éditables dans les en-têtes
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import {
  Clock, Users, BarChart2, DollarSign, Wallet, FileText,
  Camera, Wifi, Code2, Receipt, Plus, Save, Trash2,
  RefreshCw, AlertCircle, CheckSquare, Square,
  Package, Layers, ChevronDown, X, Edit2, Check, Tag,
  UserCheck, Building2, Info,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import superAdminApi from '../../services/superAdminApi';

// ============================================================================
// CONFIG
// ============================================================================

const LICENCES = [
  { key: 'basique',  label: 'Basique',  color: '#6366f1', bg: 'rgba(99,102,241,0.08)',  border: 'rgba(99,102,241,0.2)'  },
  { key: 'standard', label: 'Standard', color: '#0ea5e9', bg: 'rgba(14,165,233,0.08)',  border: 'rgba(14,165,233,0.2)'  },
  { key: 'premium',  label: 'Premium',  color: '#f59e0b', bg: 'rgba(245,158,11,0.08)',  border: 'rgba(245,158,11,0.2)'  },
];

const CATEGORIES = ['Core', 'RH', 'Avancé'];

const ICONES_MAP = {
  Clock, Users, BarChart2, DollarSign, Wallet, FileText,
  Camera, Wifi, Code2, Receipt, Package, Layers, Tag, UserCheck, Building2,
};
const ICONES_LISTE = Object.keys(ICONES_MAP);

const fmt     = (n) => Number(n || 0).toLocaleString('fr-FR') + ' FCFA';
const catColor = { Core: '#10b981', RH: '#0ea5e9', 'Avancé': '#f59e0b' };

const IconeRender = ({ name, size = 14, color }) => {
  const Ic = ICONES_MAP[name] || Package;
  return <Ic size={size} style={{ color }} />;
};

// ============================================================================
// MODAL AJOUT / ÉDITION MODULE
// ============================================================================
const ModuleFormModal = ({ module: editModule, onClose, onSaved, theme: t }) => {
  const [form, setForm] = useState({
    module_key:  editModule?.module_key  || '',
    label:       editModule?.label       || '',
    description: editModule?.description || '',
    icone:       editModule?.icone       || 'Package',
    categorie:   editModule?.categorie   || 'Core',
    ordre:       editModule?.ordre       || 0,
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  const isEdit = !!editModule;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setError('');
    if (!form.module_key.trim()) return setError('La clé technique est obligatoire.');
    if (!form.label.trim())      return setError('Le nom est obligatoire.');
    if (!/^[a-z0-9_]+$/.test(form.module_key)) return setError('Clé : minuscules, chiffres et _ uniquement.');
    setSaving(true);
    try {
      const res = isEdit
        ? await superAdminApi.licenceModules.update(editModule.module_key, form)
        : await superAdminApi.licenceModules.create(form);
      if (res.success) { onSaved(); onClose(); }
      else setError(res.message || 'Erreur.');
    } catch { setError('Erreur réseau.'); }
    finally { setSaving(false); }
  };

  const inp = {
    width: '100%', padding: '9px 12px', borderRadius: 10,
    background: t.bgInput, border: `1px solid ${t.border}`,
    color: t.text, fontSize: 13, boxSizing: 'border-box', outline: 'none',
  };
  const lbl = { fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 6 };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
      <div style={{ width: '100%', maxWidth: 500, borderRadius: 20, background: t.bgCard, border: `1px solid ${t.border}`, boxShadow: '0 24px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: t.primarySoft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Package size={16} style={{ color: t.primary }} />
            </div>
            <div>
              <p style={{ fontSize: 14, fontWeight: 700, color: t.text, margin: 0 }}>{isEdit ? 'Modifier le module' : 'Nouveau module'}</p>
              <p style={{ fontSize: 11, color: t.textMuted, margin: 0 }}>Catalogue HoraPilot</p>
            </div>
          </div>
          <button onClick={onClose} style={{ padding: 6, borderRadius: 8, background: 'none', border: 'none', cursor: 'pointer', color: t.textMuted }}><X size={16} /></button>
        </div>

        <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={lbl}>Clé technique * <span style={{ fontWeight: 400, color: t.textMuted }}>(ex: notes_frais)</span></label>
            <input value={form.module_key} onChange={e => set('module_key', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
              disabled={isEdit} placeholder="module_key"
              style={{ ...inp, opacity: isEdit ? 0.6 : 1, fontFamily: 'monospace' }} />
          </div>
          <div>
            <label style={lbl}>Nom affiché *</label>
            <input value={form.label} onChange={e => set('label', e.target.value)} placeholder="Ex: Notes de frais" style={inp} />
          </div>
          <div>
            <label style={lbl}>Description courte</label>
            <textarea rows={2} value={form.description} onChange={e => set('description', e.target.value)}
              placeholder="Affiché sur les fiches prospect..."
              style={{ ...inp, resize: 'none' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Icône</label>
              <div style={{ position: 'relative' }}>
                <select value={form.icone} onChange={e => set('icone', e.target.value)}
                  style={{ ...inp, paddingLeft: 36, appearance: 'none', cursor: 'pointer' }}>
                  {ICONES_LISTE.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                </select>
                <div style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                  <IconeRender name={form.icone} size={14} color={t.primary} />
                </div>
                <ChevronDown size={12} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: t.textMuted, pointerEvents: 'none' }} />
              </div>
            </div>
            <div>
              <label style={lbl}>Catégorie</label>
              <div style={{ position: 'relative' }}>
                <select value={form.categorie} onChange={e => set('categorie', e.target.value)}
                  style={{ ...inp, appearance: 'none', cursor: 'pointer' }}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <ChevronDown size={12} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: t.textMuted, pointerEvents: 'none' }} />
              </div>
            </div>
          </div>
          <div>
            <label style={lbl}>Ordre d'affichage</label>
            <input type="number" value={form.ordre} onChange={e => set('ordre', parseInt(e.target.value) || 0)} style={{ ...inp, width: 100 }} />
          </div>
          {error && (
            <div style={{ padding: '8px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', display: 'flex', gap: 8, alignItems: 'center' }}>
              <AlertCircle size={13} style={{ color: '#ef4444' }} />
              <span style={{ fontSize: 12, color: '#ef4444' }}>{error}</span>
            </div>
          )}
        </div>

        <div style={{ padding: '12px 20px', borderTop: `1px solid ${t.border}`, display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 9, borderRadius: 10, background: t.bgInput, border: `1px solid ${t.border}`, color: t.textSecondary, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Annuler</button>
          <button onClick={handleSave} disabled={saving}
            style={{ flex: 1, padding: 9, borderRadius: 10, background: t.primary, border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            {saving ? <span style={{ width: 13, height: 13, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} /> : <Save size={13} />}
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// CHAMP NUMÉRIQUE INLINE (pour édition tarifs dans l'en-tête)
// ============================================================================
const InlineNumberField = ({ label, value, onChange, suffix = 'FCFA', step = 1000, color, theme: t }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    <span style={{ fontSize: 9, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{label}</span>
    <div style={{ position: 'relative' }}>
      <input
        type="number" min={0} step={step} value={value || 0}
        onChange={e => onChange(parseFloat(e.target.value) || 0)}
        style={{
          width: '100%', padding: '6px 8px', borderRadius: 8, textAlign: 'right',
          background: 'rgba(255,255,255,0.08)', border: `1px solid ${color}40`,
          color, fontSize: 13, fontWeight: 800, boxSizing: 'border-box', outline: 'none',
          fontFamily: 'inherit',
        }}
        onFocus={e => { e.target.style.borderColor = color; e.target.style.boxShadow = `0 0 0 2px ${color}25`; }}
        onBlur={e  => { e.target.style.borderColor = `${color}40`; e.target.style.boxShadow = 'none'; }}
      />
    </div>
    <span style={{ fontSize: 9, color: color + 'aa', textAlign: 'right' }}>
      {Number(value || 0).toLocaleString('fr-FR')} {suffix}
    </span>
  </div>
);

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================
const LicenceModulesManager = () => {
  const t = useTheme();

  const [modules,     setModules]     = useState([]);
  const [grille,      setGrille]      = useState({});
  const [tarifs,      setTarifs]      = useState({
    basique:  { prix: 0, frais_mise_en_place: 0, max_employes: 50,  max_utilisateurs: 5,  prix_par_utilisateur_sup: 3500, prix_par_employe_sup: 2500, label: 'HoraPilot Basique'  },
    standard: { prix: 0, frais_mise_en_place: 0, max_employes: 150, max_utilisateurs: 10, prix_par_utilisateur_sup: 3500, prix_par_employe_sup: 2500, label: 'HoraPilot Standard' },
    premium:  { prix: 0, frais_mise_en_place: 0, max_employes: 500, max_utilisateurs: 20, prix_par_utilisateur_sup: 3500, prix_par_employe_sup: 2500, label: 'HoraPilot Premium'  },
  });
  const [loading,     setLoading]     = useState(true);
  const [savingGrid,  setSavingGrid]  = useState(false);
  const [savingTarifs,setSavingTarifs]= useState(false);
  const [savedGrid,   setSavedGrid]   = useState(false);
  const [savedTarifs, setSavedTarifs] = useState(false);
  const [modal,       setModal]       = useState(null);
  const [deleting,    setDeleting]    = useState(null);
  const [error,       setError]       = useState('');

  // ── Charger données ──
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [modRes, grilleRes, tarifRes] = await Promise.all([
        superAdminApi.licenceModules.getAll(),
        superAdminApi.licenceModules.getGrille(),
        superAdminApi.danicedSettings.getTarifs(),
      ]);
      if (modRes.success)    setModules(modRes.data || []);
      if (grilleRes.success) {
        const g = {};
        LICENCES.forEach(l => {
          g[l.key] = new Set((grilleRes.data[l.key] || []).map(m => m.module_key));
        });
        setGrille(g);
      }
      if (tarifRes.success && tarifRes.data) {
        const map = {};
        tarifRes.data.forEach(item => {
          map[item.type_licence] = {
            prix:                     parseFloat(item.prix)                     || 0,
            frais_mise_en_place:      parseFloat(item.frais_mise_en_place)      || 0,
            max_employes:             parseInt(item.max_employes)               || 0,
            max_utilisateurs:         parseInt(item.max_utilisateurs)           || 0,
            prix_par_utilisateur_sup: parseFloat(item.prix_par_utilisateur_sup) || 3500,
            prix_par_employe_sup:     parseFloat(item.prix_par_employe_sup)     || 2500,
            label:                    item.label || '',
          };
        });
        setTarifs(prev => ({ ...prev, ...map }));
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Toggle case grille ──
  const toggleModule = (licenceKey, moduleKey) => {
    setGrille(prev => {
      const next = { ...prev };
      const set  = new Set(next[licenceKey]);
      set.has(moduleKey) ? set.delete(moduleKey) : set.add(moduleKey);
      next[licenceKey] = set;
      return next;
    });
  };

  // ── Modifier un tarif ──
  const setTarif = (licenceKey, field, value) => {
    setTarifs(prev => ({
      ...prev,
      [licenceKey]: { ...prev[licenceKey], [field]: value },
    }));
  };

  // ── Sauvegarder grille ──
  const handleSaveGrille = async () => {
    setSavingGrid(true);
    try {
      const payload = {};
      LICENCES.forEach(l => { payload[l.key] = [...(grille[l.key] || new Set())]; });
      const res = await superAdminApi.licenceModules.saveGrille(payload);
      if (res.success) { setSavedGrid(true); setTimeout(() => setSavedGrid(false), 2500); }
    } finally { setSavingGrid(false); }
  };

  // ── Sauvegarder tarifs ──
  const handleSaveTarifs = async () => {
    setSavingTarifs(true); setError('');
    try {
      const payload = Object.entries(tarifs).map(([type_licence, v]) => ({
        type_licence,
        prix:                     v.prix,
        frais_mise_en_place:      v.frais_mise_en_place,
        max_employes:             v.max_employes,
        max_utilisateurs:         v.max_utilisateurs,
        prix_par_utilisateur_sup: v.prix_par_utilisateur_sup,
        prix_par_employe_sup:     v.prix_par_employe_sup,
        label:                    v.label,
      }));
      const res = await superAdminApi.danicedSettings.updateTarifs(payload);
      if (res.success) { setSavedTarifs(true); setTimeout(() => setSavedTarifs(false), 2500); }
      else setError(res.message || 'Erreur sauvegarde tarifs.');
    } finally { setSavingTarifs(false); }
  };

  // ── Supprimer module ──
  const handleDelete = async (moduleKey) => {
    setDeleting(moduleKey);
    try { await superAdminApi.licenceModules.delete(moduleKey); load(); }
    finally { setDeleting(null); }
  };

  // ── Grouper par catégorie ──
  const modulesByCategorie = CATEGORIES.reduce((acc, cat) => {
    acc[cat] = modules.filter(m => m.actif && m.categorie === cat);
    return acc;
  }, {});
  const autresModules = modules.filter(m => m.actif && !CATEGORIES.includes(m.categorie));

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* HEADER */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 800, color: t.text, margin: '0 0 4px' }}>Modules & Licences</h1>
          <p style={{ fontSize: 13, color: t.textMuted, margin: 0 }}>
            Configurez les modules inclus, les tarifs et les limites par licence
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setModal('add')}
            style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px', borderRadius: 10, background: t.bgInput, border: `1px solid ${t.border}`, color: t.textSecondary, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            <Plus size={14} /> Nouveau module
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', display: 'flex', gap: 8, alignItems: 'center' }}>
          <AlertCircle size={14} style={{ color: '#ef4444', flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: '#ef4444' }}>{error}</span>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 60, textAlign: 'center' }}>
          <RefreshCw size={22} style={{ color: t.primary, animation: 'spin 1s linear infinite', display: 'inline-block' }} />
          <p style={{ fontSize: 13, color: t.textMuted, marginTop: 10 }}>Chargement…</p>
        </div>
      ) : (
        <>
          {/* ══ GRILLE PRINCIPALE ══ */}
          <div style={{ borderRadius: 16, border: `1px solid ${t.border}`, overflow: 'hidden', background: t.bgCard }}>

            {/* ── EN-TÊTES COLONNES avec tarifs éditables ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '220px repeat(3, 1fr)' }}>

              {/* Colonne module — titre + bouton sauvegarder grille */}
              <div style={{ padding: '14px 16px', background: t.bgTableHeader, borderBottom: `1px solid ${t.border}`, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Module</span>
              </div>

              {/* Colonnes licence */}
              {LICENCES.map(l => (
                <div key={l.key} style={{
                  padding: '14px 16px',
                  background: l.bg,
                  borderLeft: `1px solid ${t.border}`,
                  borderBottom: `1px solid ${t.border}`,
                  display: 'flex', flexDirection: 'column', gap: 10,
                }}>
                  {/* Titre licence */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 12, fontWeight: 900, color: l.color, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                      {l.label}
                    </span>
                    <span style={{ fontSize: 10, color: l.color + '99' }}>
                      {[...((grille[l.key]) || new Set())].length} modules
                    </span>
                  </div>

                  {/* Champs tarifs éditables */}
                  <InlineNumberField
                    label="Prix mensuel"
                    value={tarifs[l.key]?.prix}
                    onChange={v => setTarif(l.key, 'prix', v)}
                    color={l.color} theme={t} step={1000}
                  />
                  <InlineNumberField
                    label="Frais de mise en place"
                    value={tarifs[l.key]?.frais_mise_en_place}
                    onChange={v => setTarif(l.key, 'frais_mise_en_place', v)}
                    color={l.color} theme={t} step={5000}
                  />

                  {/* Limites employés / utilisateurs */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontSize: 9, fontWeight: 700, color: l.color, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Employés max</span>
                      <input type="number" min={1} value={tarifs[l.key]?.max_employes || 0}
                        onChange={e => setTarif(l.key, 'max_employes', parseInt(e.target.value) || 0)}
                        style={{ width: '100%', padding: '5px 7px', borderRadius: 7, background: 'rgba(255,255,255,0.08)', border: `1px solid ${l.color}40`, color: l.color, fontSize: 12, fontWeight: 700, textAlign: 'center', boxSizing: 'border-box', outline: 'none' }}
                        onFocus={e => { e.target.style.borderColor = l.color; }}
                        onBlur={e  => { e.target.style.borderColor = `${l.color}40`; }}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontSize: 9, fontWeight: 700, color: l.color, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Utilisateurs</span>
                      <input type="number" min={1} value={tarifs[l.key]?.max_utilisateurs || 0}
                        onChange={e => setTarif(l.key, 'max_utilisateurs', parseInt(e.target.value) || 0)}
                        style={{ width: '100%', padding: '5px 7px', borderRadius: 7, background: 'rgba(255,255,255,0.08)', border: `1px solid ${l.color}40`, color: l.color, fontSize: 12, fontWeight: 700, textAlign: 'center', boxSizing: 'border-box', outline: 'none' }}
                        onFocus={e => { e.target.style.borderColor = l.color; }}
                        onBlur={e  => { e.target.style.borderColor = `${l.color}40`; }}
                      />
                    </div>
                  </div>

                  {/* Prix add-ons */}
                  <div style={{ borderTop: `1px dashed ${l.color}30`, paddingTop: 8 }}>
                    <span style={{ fontSize: 9, fontWeight: 800, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', marginBottom: 6 }}>
                      ✦ Add-ons
                    </span>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ fontSize: 8, fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase' }}>+1 user/mois</span>
                        <input type="number" min={0} step={100} value={tarifs[l.key]?.prix_par_utilisateur_sup || 0}
                          onChange={e => setTarif(l.key, 'prix_par_utilisateur_sup', parseFloat(e.target.value) || 0)}
                          style={{ width: '100%', padding: '4px 6px', borderRadius: 6, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', color: '#f59e0b', fontSize: 11, fontWeight: 700, textAlign: 'right', boxSizing: 'border-box', outline: 'none' }}
                          onFocus={e => { e.target.style.borderColor = '#f59e0b'; }}
                          onBlur={e  => { e.target.style.borderColor = 'rgba(245,158,11,0.3)'; }}
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span style={{ fontSize: 8, fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase' }}>+10 emp/mois</span>
                        <input type="number" min={0} step={100} value={tarifs[l.key]?.prix_par_employe_sup || 0}
                          onChange={e => setTarif(l.key, 'prix_par_employe_sup', parseFloat(e.target.value) || 0)}
                          style={{ width: '100%', padding: '4px 6px', borderRadius: 6, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', color: '#f59e0b', fontSize: 11, fontWeight: 700, textAlign: 'right', boxSizing: 'border-box', outline: 'none' }}
                          onFocus={e => { e.target.style.borderColor = '#f59e0b'; }}
                          onBlur={e  => { e.target.style.borderColor = 'rgba(245,158,11,0.3)'; }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* ── LIGNES MODULES par catégorie ── */}
            {CATEGORIES.map(cat => {
              const mods = modulesByCategorie[cat] || [];
              if (!mods.length) return null;
              return (
                <div key={cat}>
                  {/* Séparateur catégorie */}
                  <div style={{ padding: '5px 16px', background: t.bgInput, borderBottom: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: catColor[cat] || t.primary, flexShrink: 0 }} />
                    <span style={{ fontSize: 10, fontWeight: 800, color: catColor[cat] || t.textMuted, textTransform: 'uppercase', letterSpacing: '0.12em' }}>{cat}</span>
                  </div>

                  {mods.map((mod, i) => (
                    <div key={mod.module_key}
                      style={{ display: 'grid', gridTemplateColumns: '220px repeat(3, 1fr)', borderBottom: i < mods.length - 1 ? `1px solid ${t.borderLight}` : 'none', transition: 'background 0.1s' }}
                      onMouseEnter={e => { e.currentTarget.style.background = t.bgTableRowHover; e.currentTarget.querySelector('.row-act').style.opacity = 1; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent';     e.currentTarget.querySelector('.row-act').style.opacity = 0; }}
                    >
                      {/* Nom module */}
                      <div style={{ padding: '9px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 26, height: 26, borderRadius: 7, background: (catColor[cat] || t.primary) + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <IconeRender name={mod.icone} size={12} color={catColor[cat] || t.primary} />
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <p style={{ fontSize: 12, fontWeight: 700, color: t.text, margin: 0 }}>{mod.label}</p>
                          <code style={{ fontSize: 9, color: t.textMuted }}>{mod.module_key}</code>
                        </div>
                        <div className="row-act" style={{ display: 'flex', gap: 4, opacity: 0, transition: 'opacity 0.15s' }}>
                          <button onClick={() => setModal({ module: mod })} title="Modifier"
                            style={{ padding: 4, borderRadius: 6, background: t.primarySoft, border: 'none', color: t.primary, cursor: 'pointer', display: 'flex' }}>
                            <Edit2 size={11} />
                          </button>
                          <button onClick={() => handleDelete(mod.module_key)} disabled={deleting === mod.module_key} title="Désactiver"
                            style={{ padding: 4, borderRadius: 6, background: 'rgba(239,68,68,0.1)', border: 'none', color: '#ef4444', cursor: 'pointer', display: 'flex' }}>
                            <Trash2 size={11} />
                          </button>
                        </div>
                      </div>

                      {/* Cases à cocher */}
                      {LICENCES.map(l => {
                        const checked = grille[l.key]?.has(mod.module_key) || false;
                        return (
                          <div key={l.key} onClick={() => toggleModule(l.key, mod.module_key)}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', borderLeft: `1px solid ${t.border}`, cursor: 'pointer', background: checked ? l.bg : 'transparent', transition: 'background 0.15s' }}>
                            {checked
                              ? <CheckSquare size={18} style={{ color: l.color }} />
                              : <Square size={18} style={{ color: t.border }} />}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              );
            })}

            {/* Modules sans catégorie */}
            {autresModules.map((mod, i) => (
              <div key={mod.module_key}
                style={{ display: 'grid', gridTemplateColumns: '220px repeat(3, 1fr)', borderBottom: i < autresModules.length - 1 ? `1px solid ${t.borderLight}` : 'none' }}>
                <div style={{ padding: '9px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 26, height: 26, borderRadius: 7, background: t.primarySoft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IconeRender name={mod.icone} size={12} color={t.primary} />
                  </div>
                  <div>
                    <p style={{ fontSize: 12, fontWeight: 700, color: t.text, margin: 0 }}>{mod.label}</p>
                    <code style={{ fontSize: 9, color: t.textMuted }}>{mod.module_key}</code>
                  </div>
                </div>
                {LICENCES.map(l => {
                  const checked = grille[l.key]?.has(mod.module_key) || false;
                  return (
                    <div key={l.key} onClick={() => toggleModule(l.key, mod.module_key)}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', borderLeft: `1px solid ${t.border}`, cursor: 'pointer', background: checked ? l.bg : 'transparent' }}>
                      {checked ? <CheckSquare size={18} style={{ color: l.color }} /> : <Square size={18} style={{ color: t.border }} />}
                    </div>
                  );
                })}
              </div>
            ))}

            {modules.filter(m => m.actif).length === 0 && (
              <div style={{ padding: 40, textAlign: 'center', color: t.textMuted }}>
                <Package size={32} style={{ margin: '0 auto 10px', opacity: 0.3 }} />
                <p style={{ fontSize: 14, fontWeight: 600 }}>Aucun module dans le catalogue</p>
                <p style={{ fontSize: 12, marginTop: 4 }}>Ajoutez votre premier module avec le bouton ci-dessus</p>
              </div>
            )}
          </div>

          {/* ── BOUTONS SAUVEGARDER ── */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>

            {/* Info */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 10, background: t.primarySofter, border: `1px solid ${t.primaryBorder}` }}>
              <Info size={13} style={{ color: t.primary, flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: t.textSecondary }}>
                Les modifications des tarifs sont indépendantes de la grille des modules. Sauvegardez séparément.
              </span>
            </div>

            {/* Sauvegarder tarifs */}
            <button onClick={handleSaveTarifs} disabled={savingTarifs}
              style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '10px 18px', borderRadius: 10, background: savedTarifs ? '#10b981' : '#f59e0b', border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: savingTarifs ? 'not-allowed' : 'pointer', opacity: savingTarifs ? 0.7 : 1, transition: 'background 0.3s', whiteSpace: 'nowrap' }}>
              {savingTarifs
                ? <span style={{ width: 13, height: 13, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
                : savedTarifs ? <Check size={14} /> : <DollarSign size={14} />}
              {savingTarifs ? 'Sauvegarde...' : savedTarifs ? 'Tarifs sauvegardés !' : 'Sauvegarder les tarifs'}
            </button>

            {/* Sauvegarder grille */}
            <button onClick={handleSaveGrille} disabled={savingGrid}
              style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '10px 18px', borderRadius: 10, background: savedGrid ? '#10b981' : t.primary, border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: savingGrid ? 'not-allowed' : 'pointer', opacity: savingGrid ? 0.7 : 1, transition: 'background 0.3s', whiteSpace: 'nowrap' }}>
              {savingGrid
                ? <span style={{ width: 13, height: 13, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
                : savedGrid ? <Check size={14} /> : <Save size={14} />}
              {savingGrid ? 'Sauvegarde...' : savedGrid ? 'Grille sauvegardée !' : 'Sauvegarder la grille'}
            </button>
          </div>
        </>
      )}

      {/* MODALS */}
      {modal === 'add' && (
        <ModuleFormModal onClose={() => setModal(null)} onSaved={load} theme={t} />
      )}
      {modal?.module && (
        <ModuleFormModal module={modal.module} onClose={() => setModal(null)} onSaved={load} theme={t} />
      )}

      <style>{`
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
      `}</style>
    </div>
  );
};

export default LicenceModulesManager;